/**
 * Before & After — Custom Element
 * Draggable vertical divider for image comparison.
 * requestAnimationFrame-based, multi-instance safe, no dependencies.
 */
if (!customElements.get('before-after-compare')) {
  class BeforeAfterCompare extends HTMLElement {
    constructor() {
      super();
      this._dragging = false;
      this._rafId = null;
      this._pos = 50;
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
    }

    connectedCallback() {
      this._images = this.querySelector('.before-after__images');
      this._divider = this.querySelector('.before-after__divider');
      this._productBefore = this.querySelector('.before-after__layer--before .before-after__product');
      this._productAfter = this.querySelector('.before-after__layer--after .before-after__product');

      const initial = parseFloat(this.dataset.initialPosition) || 50;
      this._pos = Math.max(0, Math.min(100, initial));
      this._applyPosition();

      this._images.addEventListener('pointerdown', this._onPointerDown);
    }

    disconnectedCallback() {
      if (this._images) {
        this._images.removeEventListener('pointerdown', this._onPointerDown);
      }
      this._cancelRaf();
    }

    _onPointerDown(e) {
      /* Allow clicks on product links to pass through */
      if (e.target.closest('.before-after__product')) return;

      e.preventDefault();
      this._dragging = true;
      this._images.setPointerCapture(e.pointerId);
      this._images.addEventListener('pointermove', this._onPointerMove);
      this._images.addEventListener('pointerup', this._onPointerUp);
      this._images.addEventListener('pointercancel', this._onPointerUp);
      this._updateFromEvent(e);
    }

    _onPointerMove(e) {
      if (!this._dragging) return;
      this._updateFromEvent(e);
    }

    _onPointerUp(e) {
      this._dragging = false;
      if (this._images) {
        this._images.releasePointerCapture(e.pointerId);
        this._images.removeEventListener('pointermove', this._onPointerMove);
        this._images.removeEventListener('pointerup', this._onPointerUp);
        this._images.removeEventListener('pointercancel', this._onPointerUp);
      }
      this._cancelRaf();
    }

    _updateFromEvent(e) {
      const rect = this._images.getBoundingClientRect();
      const x = e.clientX - rect.left;
      this._pos = Math.max(0, Math.min(100, (x / rect.width) * 100));

      if (!this._rafId) {
        this._rafId = requestAnimationFrame(() => {
          this._applyPosition();
          this._rafId = null;
        });
      }
    }

    _applyPosition() {
      const pos = this._pos;
      this._images.style.setProperty('--divider-pos', pos + '%');

      /* Product visibility: fade out as the divider moves away */
      if (this._productBefore) {
        /* Before product fades when divider < 20% (image mostly hidden) */
        const beforeOpacity = pos <= 15 ? 0 : pos <= 30 ? (pos - 15) / 15 : 1;
        this._productBefore.style.opacity = beforeOpacity;
        this._productBefore.style.pointerEvents = beforeOpacity === 0 ? 'none' : '';
      }

      if (this._productAfter) {
        /* After product fades when divider > 80% (image mostly hidden) */
        const afterOpacity = pos >= 85 ? 0 : pos >= 70 ? (85 - pos) / 15 : 1;
        this._productAfter.style.opacity = afterOpacity;
        this._productAfter.style.pointerEvents = afterOpacity === 0 ? 'none' : '';
      }
    }

    _cancelRaf() {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
    }
  }

  customElements.define('before-after-compare', BeforeAfterCompare);
}
